import type { ReactNode } from "react";

export type AppId =
  | "portal"
  | "crm"
  | "board"
  | "project"
  | "axion"
  | "docs"
  | "fastighet"
  | "school"
  | "gym"
  | "hr"
  | "nexus"
  | "procure"
  | "sustain"
  | "parties";

export type AppRegistryEntry = {
  id: AppId;
  title: string;
  titleKey: string;
  href: string;
  baseUrl: string;
  icon: ReactNode;
  group: string;
  access?: AppRegistryAccess;
  disabled?: boolean;
};

export type AppRegistryOverrides = {
  baseUrls?: Partial<Record<AppId, string>>;
};

export type AppRegistryAccess = {
  anyRoles?: string[];
  anyScopes?: string[];
};

export type AppRegistryAccessContext = {
  isAuthenticated?: boolean;
  roles?: string[];
  scopes?: string[];
  currentAppId?: AppId | string;
};

type AppIconProps = {
  className?: string;
};

const PortalIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const CrmIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M4 12c0-3.3 2.7-6 6-6 1.8 0 3.5.8 4.6 2.1A5.3 5.3 0 0 1 20 13.3C20 17 17 20 13.4 20H10c-3.3 0-6-2.7-6-6Z"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M9 12h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const BoardIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M4 20c1.4-3.4 4.2-5 8-5s6.6 1.6 8 5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const ProjectIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M4 7.5c0-1.1.9-2 2-2h4l2 2h6c1.1 0 2 .9 2 2v7.5c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V7.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const AxionIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="18" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 7.5 10.5 15" stroke="currentColor" strokeWidth="1.6" />
    <path d="M16 7.5 13.5 15" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const DocsIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M7 4h7l4 4v12c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M14 4v4h4" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const FastighetIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M5 20V9l7-5 7 5v11"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M9 20v-5h6v5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const HrIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3.5 19c.9-2.7 3.1-4.1 6.5-4.1" stroke="currentColor" strokeWidth="1.6" />
    <path d="M14 7h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M14 12h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const GymIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d="M5 9v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M9 8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M15 8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M19 9v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M9 12h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const SchoolIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M4 20V9.5L12 5l8 4.5V20"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M8 20v-6h8v6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9 10h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const NexusIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M12 3 19.5 7.5v9L12 21 4.5 16.5v-9L12 3Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const ProcureIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M5 6h2l1.6 8.5h8.8L19 8H9"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="19" r="1.6" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="17" cy="19" r="1.6" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const SustainIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M5 14c4-7 10-9 14-9-1 5-3 10-9 14-2 1.3-4 1-5.5.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M7 17c1.5-1.5 3-2.5 5-3" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const PartiesIcon = ({ className }: AppIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="16.5" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M3.5 19c.9-2.7 3.1-4.1 6.5-4.1 2.2 0 3.9.6 5.1 1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M14 15.2c2 .3 3.4 1.5 4.1 3.8" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const portalProductionBaseUrl = "https://portal.stenpro.se";

const portalModuleUrl = (moduleKey: string) => `${portalProductionBaseUrl}/open/${moduleKey}`;

const productionBaseUrls: Record<AppId, string> = {
  portal: portalProductionBaseUrl,
  crm: "http://192.168.16.24:6104",
  board: "https://board.svenskastenhus.se",
  project: "https://project.svenskastenhus.se",
  axion: portalModuleUrl("axion"),
  docs: "https://docs.svenskastenhus.se",
  fastighet: "https://fastighet.svenskastenhus.se",
  school: portalModuleUrl("school"),
  gym: "https://gym.svenskastenhus.se",
  hr: portalModuleUrl("hr"),
  nexus: portalModuleUrl("nexus"),
  procure: portalModuleUrl("procure"),
  sustain: "https://sustain.svenskastenhus.se",
  parties: "http://192.168.16.24:6110",
};

const azureBaseUrls: Record<AppId, string> = {
  portal: "https://portal-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  crm: "https://crm-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  board: "https://board-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  project: "https://project-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  axion: "https://axion-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  docs: "https://docs-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  fastighet: "https://fastighet-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  school: "https://school-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  gym: "https://gym-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  hr: "https://hr-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  nexus: "https://nexus-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  procure: "https://procure-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  sustain: "https://sustain-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
  parties: "https://parties-frontend.frontend.whiterock-b0da9de3.swedencentral.azurecontainerapps.io",
};

const dockerBaseUrls: Record<AppId, string> = {
  portal: "http://localhost:3000",
  docs: "http://localhost:5173",
  axion: "http://localhost:5174",
  hr: "http://localhost:5175",
  fastighet: "http://localhost:5176",
  school: "http://localhost:5185",
  gym: "http://localhost:5195",
  procure: "http://localhost:5177",
  nexus: "http://localhost:5178",
  project: "http://localhost:5179",
  sustain: "http://localhost:5180",
  board: "http://localhost:5181",
  crm: "http://localhost:5182",
  parties: "http://localhost:5184",
};

const accessByAppId: Record<AppId, AppRegistryAccess> = {
  portal: {},
  crm: {
    anyRoles: ["crm.admin", "crm.user"],
    anyScopes: ["crm.read"],
  },
  board: {
    anyRoles: ["board.admin", "board.user"],
    anyScopes: ["board.read"],
  },
  project: {
    anyRoles: ["project.owner", "project.member", "project.user", "project.admin", "ProjectManager", "Administrator"],
    anyScopes: ["project.read"],
  },
  axion: {
    anyRoles: ["axion.viewer", "axion.editor"],
    anyScopes: ["axion.viewer"],
  },
  docs: {
    anyRoles: ["docs.read", "docs.write"],
    anyScopes: ["docs.read"],
  },
  fastighet: {
    anyRoles: ["fastighet.user", "fastighet.admin", "Admin"],
    anyScopes: ["fastighet.read"],
  },
  school: {
    anyRoles: ["school.admissions.read", "school.admissions.write", "school.offers.manage"],
    anyScopes: ["school.admissions.read"],
  },
  gym: {
    anyRoles: ["gym.admin", "gym.user"],
    anyScopes: ["gym.read"],
  },
  hr: {
    anyRoles: ["hr.admin", "hr.user"],
    anyScopes: ["hr.read"],
  },
  nexus: {
    anyRoles: ["nexus.finance", "nexus.bokforing"],
    anyScopes: ["nexus.finance"],
  },
  procure: {
    anyRoles: ["procure.manager", "procure.approver"],
    anyScopes: ["procure.read"],
  },
  sustain: {
    anyRoles: ["sustain.editor", "sustain.viewer"],
    anyScopes: ["sustain.viewer"],
  },
  parties: {
    anyRoles: ["parties.admin", "platform.admin", "stenpro.admin"],
    anyScopes: ["parties.admin"],
  },
};

const resolveAppSwitcherEnv = () => {
  if (typeof window !== "undefined") {
    const win = window as unknown as { __APP_SWITCHER_ENV?: string };
    if (win.__APP_SWITCHER_ENV) return win.__APP_SWITCHER_ENV;
  }
  const metaEnv =
    typeof import.meta !== "undefined"
      ? (import.meta as unknown as { env?: Record<string, string> }).env
      : undefined;
  return (
    metaEnv?.VITE_APP_SWITCHER_ENV ??
    (globalThis as unknown as { process?: { env?: Record<string, string> } }).process?.env
      ?.NEXT_PUBLIC_APP_SWITCHER_ENV
  );
};

const resolveAppBaseUrlOverride = (appId: AppId) => {
  const key = `APP_SWITCHER_${appId.toUpperCase()}_URL`;
  const viteKey = `VITE_${key}`;
  const nextKey = `NEXT_PUBLIC_${key}`;

  if (typeof window !== "undefined") {
    const win = window as unknown as {
      __APP_SWITCHER_BASE_URLS?: Partial<Record<AppId, string>>;
    };
    const runtimeValue = win.__APP_SWITCHER_BASE_URLS?.[appId]?.trim();
    if (runtimeValue) return runtimeValue;
  }

  const metaEnv =
    typeof import.meta !== "undefined"
      ? (import.meta as unknown as { env?: Record<string, string> }).env
      : undefined;
  const processEnv = (globalThis as unknown as { process?: { env?: Record<string, string> } }).process
    ?.env;
  return metaEnv?.[viteKey]?.trim() || processEnv?.[nextKey]?.trim();
};

const isLocalhost = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host === "localhost" || host === "127.0.0.1";
};

const getDefaultBaseUrls = (): Record<AppId, string> => {
  const override = resolveAppSwitcherEnv()?.toLowerCase();
  if (override === "local" || override === "docker") return dockerBaseUrls;
  if (override === "prod" || override === "production") return productionBaseUrls;
  if (override === "azure") return azureBaseUrls;
  return isLocalhost() ? dockerBaseUrls : productionBaseUrls;
};

export const createAppRegistry = (overrides?: AppRegistryOverrides): AppRegistryEntry[] => {
  const configuredBaseUrls = getDefaultBaseUrls();
  const envBaseUrls = Object.fromEntries(
    (Object.keys(configuredBaseUrls) as AppId[])
      .map((appId) => [appId, resolveAppBaseUrlOverride(appId)] as const)
      .filter((entry): entry is [AppId, string] => Boolean(entry[1]))
  ) as Partial<Record<AppId, string>>;
  const baseUrls = { ...configuredBaseUrls, ...envBaseUrls, ...(overrides?.baseUrls ?? {}) };

  return [
    {
      id: "portal",
      title: "Portal",
      titleKey: "app.title.portal",
      baseUrl: baseUrls.portal,
      href: baseUrls.portal,
      icon: <PortalIcon className="h-8 w-8" />,
      group: "core",
      access: accessByAppId.portal,
    },
    {
      id: "crm",
      title: "CRM",
      titleKey: "app.title.crm",
      baseUrl: baseUrls.crm,
      href: baseUrls.crm,
      icon: <CrmIcon className="h-8 w-8" />,
      group: "core",
      access: accessByAppId.crm,
    },
    {
      id: "board",
      title: "Board",
      titleKey: "app.title.board",
      baseUrl: baseUrls.board,
      href: baseUrls.board,
      icon: <BoardIcon className="h-8 w-8" />,
      group: "core",
      access: accessByAppId.board,
    },
    {
      id: "project",
      title: "Project",
      titleKey: "app.title.project",
      baseUrl: baseUrls.project,
      href: baseUrls.project,
      icon: <ProjectIcon className="h-8 w-8" />,
      group: "core",
      access: accessByAppId.project,
    },
    {
      id: "axion",
      title: "Axion",
      titleKey: "app.title.axion",
      baseUrl: baseUrls.axion,
      href: baseUrls.axion,
      icon: <AxionIcon className="h-8 w-8" />,
      group: "core",
      access: accessByAppId.axion,
    },
    {
      id: "docs",
      title: "Docs",
      titleKey: "app.title.docs",
      baseUrl: baseUrls.docs,
      href: baseUrls.docs,
      icon: <DocsIcon className="h-8 w-8" />,
      group: "core",
      access: accessByAppId.docs,
    },
    {
      id: "fastighet",
      title: "Fastighet",
      titleKey: "app.title.fastighet",
      baseUrl: baseUrls.fastighet,
      href: baseUrls.fastighet,
      icon: <FastighetIcon className="h-8 w-8" />,
      group: "operations",
      access: accessByAppId.fastighet,
    },
    {
      id: "school",
      title: "School",
      titleKey: "app.title.school",
      baseUrl: baseUrls.school,
      href: baseUrls.school,
      icon: <SchoolIcon className="h-8 w-8" />,
      group: "operations",
      access: accessByAppId.school,
    },
    {
      id: "gym",
      title: "Gym",
      titleKey: "modules.gym.title",
      baseUrl: baseUrls.gym,
      href: baseUrls.gym,
      icon: <GymIcon className="h-8 w-8" />,
      group: "operations",
      access: accessByAppId.gym,
    },
    {
      id: "hr",
      title: "HR",
      titleKey: "app.title.hr",
      baseUrl: baseUrls.hr,
      href: baseUrls.hr,
      icon: <HrIcon className="h-8 w-8" />,
      group: "operations",
      access: accessByAppId.hr,
    },
    {
      id: "nexus",
      title: "Nexus",
      titleKey: "app.title.nexus",
      baseUrl: baseUrls.nexus,
      href: baseUrls.nexus,
      icon: <NexusIcon className="h-8 w-8" />,
      group: "operations",
      access: accessByAppId.nexus,
    },
    {
      id: "procure",
      title: "ProCure",
      titleKey: "app.title.procure",
      baseUrl: baseUrls.procure,
      href: baseUrls.procure,
      icon: <ProcureIcon className="h-8 w-8" />,
      group: "operations",
      access: accessByAppId.procure,
    },
    {
      id: "sustain",
      title: "Sustain",
      titleKey: "app.title.sustain",
      baseUrl: baseUrls.sustain,
      href: baseUrls.sustain,
      icon: <SustainIcon className="h-8 w-8" />,
      group: "operations",
      access: accessByAppId.sustain,
    },
    {
      id: "parties",
      title: "Identity",
      titleKey: "app.title.parties",
      baseUrl: baseUrls.parties,
      href: baseUrls.parties,
      icon: <PartiesIcon className="h-8 w-8" />,
      group: "platform",
      access: accessByAppId.parties,
    },
  ];
};

export const appRegistry = createAppRegistry();

const normalizeAccessSet = (values?: string[]) =>
  new Set((values ?? []).map((value) => value.trim().toLowerCase()).filter(Boolean));

const hasAny = (actual: Set<string>, required?: string[]) =>
  (required ?? []).some((value) => actual.has(value.trim().toLowerCase()));

export const canAccessApp = (app: AppRegistryEntry, context: AppRegistryAccessContext = {}) => {
  if (app.id === "portal" || app.id === context.currentAppId) return true;
  if (context.isAuthenticated === false) return false;

  const requiredRoles = app.access?.anyRoles ?? [];
  const requiredScopes = app.access?.anyScopes ?? [];
  if (requiredRoles.length === 0 && requiredScopes.length === 0) return true;

  const roles = normalizeAccessSet(context.roles);
  const scopes = normalizeAccessSet(context.scopes);
  return hasAny(roles, requiredRoles) || hasAny(scopes, requiredScopes);
};

export const filterAppRegistryByAccess = (
  apps: AppRegistryEntry[],
  context: AppRegistryAccessContext = {}
) => apps.filter((app) => canAccessApp(app, context));
